/**
 * Maps RBAC permission resource keys to feature flags (single source of truth for
 * tenant-visible capabilities). Used by the permissions matrix and aligned with
 * FeatureFlagContext.isPageEnabled / mobile flags.
 */

/** Permission keys always shown when module:admin is enabled (no dedicated page flag). */
const ADMIN_KEYS_IF_ADMIN_MODULE = new Set([
  'admin/profile',
])

/**
 * Platform console only (admin.* host / frontend-admin) — not assignable on tenant sites.
 * Matches Layout.jsx platformConsoleOnly and platformAdminHost.js.
 */
export const PLATFORM_ONLY_PERMISSION_KEYS = new Set([
  'admin/platform-config',
  'admin/billable-usage',
  'admin/tenants',
])

/** Feature flags that gate platform-console pages (not tenant org admin). */
export const PLATFORM_ONLY_FLAG_KEYS = new Set([
  'page:admin/platform-config',
  'page:admin/billable-usage',
  'page:admin/tenants',
])

/**
 * API/action permissions with no nav row — assign via the parent page row instead.
 * e.g. watchlist:create-case → monitor-safe/watch-list
 */
export const MATRIX_HIDDEN_PERMISSION_KEYS = new Set([
  'watchlist',
  'case.smr',
  'aml',
  'reports',
  'alerts',
  'cases',
  'dashboard',
  'kyc',
  'venues',
  'staff',
  'egm',
  'users',
  'settings',
  'monitor-safe/cases',
  'monitor-safe/registry',
  'compliance-safe/cases',
  'monitor-safe/builder/combined',
  'monitor-safe/builder/gaming',
  'monitor-safe/builder/transaction',
  'admin/*',
])

/** Matrix label overrides where RBAC key ≠ sidebar title */
export const PERMISSION_DISPLAY_LABELS = {
  rules: 'Rules Management',
  'compliance-safe/investigations': 'Investigation',
  'admin/typologies': 'Typology & scoring',
  'admin/form-builder': 'Form Builder',
  'compliance-safe/forms': 'Forms',
}

/**
 * Matrix display row → underlying RBAC resource (same permission, second nav link).
 * Prefer PERMISSION_ACTION_ALIASES when only some actions should alias.
 */
export const PERMISSION_ROW_ALIASES = {}

/**
 * Matrix row shows parent page key; RBAC may use a legacy resource for some actions.
 * { rowKey: { action: { resource, action } } }
 */
export const PERMISSION_ACTION_ALIASES = {
  'monitor-safe/watch-list': {
    'create-case': { resource: 'watchlist', action: 'create-case' },
  },
  'compliance-safe/smr-registry': {
    download: { resource: 'case.smr', action: 'download' },
    'form-edit': { resource: 'case.smr', action: 'edit' },
    'case-submit': { resource: 'case.smr', action: 'submit' },
    'reports-view': { resource: 'reports', action: 'view' },
    regulatory: { resource: 'reports', action: 'regulatory' },
  },
  'profile-safe/aml-screening': {
    screen: { resource: 'aml', action: 'screen' },
  },
  'compliance-safe/investigations': {
    'case-edit': { resource: 'monitor-safe/cases', action: 'edit' },
  },
  'compliance-safe/investigations/pipeline': {
    view: { resource: 'compliance-safe/investigations', action: 'view' },
  },
}

/** Resolve resource:action stored in role permissions for a matrix cell. */
export function resolvePermissionCell(resource, action) {
  const rowResource = PERMISSION_ROW_ALIASES[resource] || resource
  const alias = PERMISSION_ACTION_ALIASES[rowResource]?.[action]
  if (alias) return alias
  if (rowResource !== resource) return { resource: rowResource, action }
  return { resource, action }
}

export function permissionCellKey(resource, action) {
  const { resource: r, action: a } = resolvePermissionCell(resource, action)
  return `${r}:${a}`
}

/**
 * Permission resource key → primary feature flag key.
 * When absent, defaults to page:{permissionKey} if that flag exists in the map.
 */
const PERMISSION_TO_FLAG = {
  'executive/dashboard': 'page:executive',
  'rules': 'page:monitor-safe/rules',
  'ecdd': 'page:compliance-safe/ecdd',
  'case.smr': 'page:compliance-safe/smr-registry',
  'watchlist': 'page:monitor-safe/watch-list',
  'profile-safe/checks': 'page:profile-safe/verification-queue',
  'admin/system': 'page:monitor-safe/daily-data/settings',
  'profile-safe/kyc-registry': 'page:compliance-safe/kyc-registry',
  'compliance-safe/investigations/pipeline': 'page:compliance-safe/investigations/pipeline',
  'compliance-safe/patron-screening': 'page:compliance-safe/settings/patron-screening',
  'compliance-safe/settings/screening-keys': 'page:compliance-safe/settings/screening-keys',
  'admin/typologies': 'page:admin/typologies',
  'admin/form-builder': 'page:admin/form-builder',
  'compliance-safe/run': 'page:compliance-safe/run',
  'compliance-safe/forms': 'page:compliance-safe/forms',
  'compliance-safe/forms/pipeline': 'page:compliance-safe/forms/pipeline',
  'compliance-safe/forms/records': 'page:compliance-safe/forms/records',
  'aml': 'page:profile-safe/aml-screening',
  'reports': 'page:compliance-safe/smr-registry',
  'mobile/home': 'mobile:home',
  'mobile/payout': 'mobile:payout',
  'mobile/incident': 'mobile:incident',
  'mobile/alerts': 'mobile:alerts',
  'mobile/config': 'module:mobile',
}

function moduleForPermissionKey(permissionKey) {
  const key = String(permissionKey || '')
  if (key.startsWith('mobile/')) return 'mobile'
  if (key.startsWith('profile-safe')) return 'profile-safe'
  if (key.startsWith('monitor-safe')) return 'monitor-safe'
  if (key === 'admin/form-builder' || key === 'compliance-safe/forms' || key.startsWith('compliance-safe/forms/')) return 'forms'
  if (key.startsWith('compliance-safe')) return 'compliance-safe'
  if (key === 'admin/typologies') return 'compliance-safe'
  if (key.startsWith('governance-safe') || key.startsWith('governance.')) return 'governance-safe'
  if (key.startsWith('payout-safe')) return 'payout-safe'
  if (key.startsWith('admin/') || key === 'admin/*') return 'admin'
  if (key.startsWith('executive/')) return 'executive'
  const head = key.split('/')[0]
  const legacy = {
    ecdd: 'compliance-safe',
    'case.smr': 'compliance-safe',
    rules: 'monitor-safe',
    watchlist: 'monitor-safe',
  }
  return legacy[head] || head
}

/**
 * Resolve the feature flag key that gates a permission resource.
 * @param {string} permissionKey - e.g. 'compliance-safe/ecdd', 'mobile/payout', 'rules'
 * @returns {string}
 */
export function resolveFlagKeyForPermission(permissionKey) {
  const key = String(permissionKey || '')
  if (PERMISSION_TO_FLAG[key]) return PERMISSION_TO_FLAG[key]
  if (key.startsWith('mobile/')) {
    const slug = key.split('/')[1]
    return slug ? `mobile:${slug}` : 'module:mobile'
  }
  return `page:${key}`
}

/**
 * Shared flag check for sidebar and permissions matrix.
 * - Explicit `true` → visible
 * - Explicit `false` → hidden (tenant enable/disable is respected)
 * - Missing key → fail-open (matches sidebar until full catalog is on every tenant)
 * - Empty map → hidden (flags not loaded yet)
 */
export function resolveFlagEnabled(flags, flagKey) {
  if (!flagKey) return true
  if (!flags || Object.keys(flags).length === 0) return false
  if (flags[flagKey] !== undefined) return flags[flagKey] === true
  return true
}

/**
 * Whether a permission matrix row should be visible for this tenant's resolved flags.
 * Fail-closed: unregistered or disabled flags hide the row.
 *
 * @param {{ key?: string }} def - Permission definition row
 * @param {Record<string, boolean>} flags - Resolved tenant feature flags
 * @param {{ flagsLoading?: boolean, tenantSite?: boolean }} [options]
 * @returns {boolean}
 */
export function isPermissionVisibleForFlags(def, flags, options = {}) {
  if (options.flagsLoading) return true

  const key = def?.key || ''
  if (!key) return false

  if (MATRIX_HIDDEN_PERMISSION_KEYS.has(key)) return false

  if (options.tenantSite && PLATFORM_ONLY_PERMISSION_KEYS.has(key)) return false

  if (!flags || Object.keys(flags).length === 0) return false

  if (options.tenantSite) {
    const primaryFlag = resolveFlagKeyForPermission(key)
    if (PLATFORM_ONLY_FLAG_KEYS.has(primaryFlag)) return false
  }

  const moduleName = moduleForPermissionKey(key)
  const moduleFlag = `module:${moduleName}`

  if (!resolveFlagEnabled(flags, moduleFlag)) return false

  if (ADMIN_KEYS_IF_ADMIN_MODULE.has(key)) {
    return resolveFlagEnabled(flags, 'module:admin')
  }

  const primaryFlag = resolveFlagKeyForPermission(key)

  if (resolveFlagEnabled(flags, primaryFlag)) return true

  const directPageFlag = `page:${key}`
  if (primaryFlag !== directPageFlag && resolveFlagEnabled(flags, directPageFlag)) return true

  return false
}

export { PERMISSION_TO_FLAG, moduleForPermissionKey }
